<script lang="ts">
  import { refLabel } from "../../api/citations";
  import { renderMarkdownWithCitations } from "../../api/markdown";
  import { app } from "../../state/app.svelte";
  import { exportResponseAsDoc } from "../../api/exportConversation";
  import { renderMermaidBlocks } from "../mermaid";
  import { renderFileCards } from "../fileCards";
  import AgentActivity from "./AgentActivity.svelte";
  import type { Message } from "../../db/types";
  import { i18n } from "../../i18n/i18n.svelte";

  interface Props {
    message: Message;
  }

  let { message }: Props = $props();

  const fr = {
    exportFallback: "Réponse",
    exportSuffix: " - réponse",
    attachment: "pièce jointe",
    attachedDoc: "Document chargé dans la conversation",
    passages: (n: number) => `Passages consultés (${n})`,
    openPassage: "Ouvrir le passage",
    copyTitle: "Copier la réponse (texte)",
    copied: "✓ Copié",
    copy: "⧉ Copier",
    downloadTitle: "Télécharger cette réponse au format Word (.doc)",
  };
  const en: typeof fr = {
    exportFallback: "Answer",
    exportSuffix: " - answer",
    attachment: "attachment",
    attachedDoc: "Document loaded into the conversation",
    passages: (n) => `Passages consulted (${n})`,
    openPassage: "Open the passage",
    copyTitle: "Copy the answer (text)",
    copied: "✓ Copied",
    copy: "⧉ Copy",
    downloadTitle: "Download this answer as a Word file (.doc)",
  };
  const t = $derived(i18n.locale === "en" ? en : fr);

  let bubbleEl = $state<HTMLDivElement | undefined>();
  let copied = $state(false);
  let downloading = $state(false);
  let copyTimer: ReturnType<typeof setTimeout> | undefined;

  // Title used for the single-response .doc filename — reuse the conversation
  // title when there is one so exported files stay recognisable.
  const exportTitle = $derived(
    (app.conversations.find((c) => c.id === message.conversationId)?.title?.trim() ||
      t.exportFallback) + t.exportSuffix,
  );

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(message.content);
      copied = true;
      clearTimeout(copyTimer);
      copyTimer = setTimeout(() => (copied = false), 1500);
    } catch (err) {
      app.lastError = err instanceof Error ? err.message : String(err);
    }
  }

  async function onDownload() {
    if (downloading || !bubbleEl) return;
    downloading = true;
    try {
      await exportResponseAsDoc(bubbleEl, exportTitle);
    } catch (err) {
      app.lastError = err instanceof Error ? err.message : String(err);
    } finally {
      downloading = false;
    }
  }

  const html = $derived(
    message.role === "user"
      ? null
      : renderMarkdownWithCitations(
          message.content,
          message.retrievedChunks ?? null,
        ),
  );

  let textEl = $state<HTMLDivElement | undefined>();

  $effect(() => {
    void html;
    if (textEl) {
      void renderMermaidBlocks(textEl);
      const pendingKey = `pending:${message.conversationId}`;
      renderFileCards(
        textEl,
        message.content,
        message.retrievedChunks,
        { messageKey: message.id === "pending" ? pendingKey : message.id, pendingKey },
        (m) => (app.lastError = m),
        (m) => (app.lastWarning = m),
      );
    }
  });

  function onCitationClick(e: MouseEvent) {
    const target = e.target as HTMLElement | null;
    const citation = target?.closest<HTMLElement>(".citation[data-refs]");
    if (!citation) return;
    const nums = citation.dataset.refs
      ?.split(",")
      .map(Number)
      .filter((n) => !Number.isNaN(n));
    if (!nums || !message.retrievedChunks) return;
    const byNumber = new Map(
      message.retrievedChunks.map((r) => [r.number, r]),
    );
    const refs = nums
      .map((n) => byNumber.get(n))
      .filter((r): r is NonNullable<typeof r> => !!r);
    if (refs.length > 0) {
      const quote = citation.dataset.quote ?? null;
      app.openCitation(refs, quote, message.retrievedChunks ?? null);
    }
  }

  function openFromSource(num: number) {
    const ref = message.retrievedChunks?.find((r) => r.number === num);
    if (ref) app.openCitation([ref]);
  }
</script>

<div class="bubble {message.role}" bind:this={bubbleEl}>
  {#if message.role === "user"}
    {#if message.content}
      <div class="text user">{message.content}</div>
    {/if}
    {#if message.images && message.images.length > 0}
      <div class="att-images">
        <!-- Clé = position, pas contenu : deux images identiques dans un même
             message font lever each_key_duplicate à Svelte, y compris en prod,
             et la liste des messages cesse alors de s'afficher. -->
        {#each message.images as src, i (i)}
          <img class="att-image" {src} alt={t.attachment} />
        {/each}
      </div>
    {/if}
    {#if message.attachedDocs && message.attachedDocs.length > 0}
      <div class="att-docs">
        {#each message.attachedDocs as doc, i (i)}
          <span class="att-doc" title={t.attachedDoc}>
            📄 {doc.name} · <span class="att-doc-tk">~{Math.round(doc.tokens / 1000) || 1} k tk</span>
          </span>
        {/each}
      </div>
    {/if}
  {:else}
    {#if message.activity && message.activity.length > 0}
      <AgentActivity steps={message.activity} />
    {/if}
    <!-- Re-created on a language switch so the Mermaid toolbars (built in
         plain DOM by mermaid.ts) are rebuilt in the new language. -->
    {#key i18n.locale}
      <div
        bind:this={textEl}
        class="text assistant"
        role="presentation"
        onmousedown={onCitationClick}
        onkeydown={(e) => {
          if (e.key === "Enter" || e.key === " ") onCitationClick(e as unknown as MouseEvent);
        }}
      >{@html html}</div>
    {/key}
    {#if message.retrievedChunks && message.retrievedChunks.length > 0}
      <details class="sources">
        <summary>{t.passages(message.retrievedChunks.length)}</summary>
        <ul>
          {#each message.retrievedChunks as ref (ref.number)}
            <li>
              <button
                class="source-row"
                onclick={() => openFromSource(ref.number)}
                title={t.openPassage}
              >
                <span class="ref-num">[{ref.number}]</span>
                <span class="ref-name">{refLabel(ref)}</span>
                <span class="ref-score">{(ref.score * 100).toFixed(0)}%</span>
              </button>
            </li>
          {/each}
        </ul>
      </details>
    {/if}
    {#if message.content && message.id !== "pending"}
      <div class="msg-actions">
        <button
          type="button"
          onclick={onCopy}
          title={t.copyTitle}
        >{copied ? t.copied : t.copy}</button>
        <button
          type="button"
          onclick={onDownload}
          disabled={downloading}
          title={t.downloadTitle}
        >{downloading ? "…" : "⬇ Word"}</button>
      </div>
    {/if}
  {/if}
</div>

<style>
  .bubble {
    padding: 0.6rem 0.8rem;
    border-radius: var(--epfl-radius);
    max-width: 100%;
    font-size: 0.88rem;
    line-height: 1.45;
  }

  .bubble.user {
    background: var(--epfl-white);
    border: 1px solid var(--epfl-gray-200);
    border-left: 3px solid var(--epfl-accent);
    align-self: flex-end;
    max-width: 85%;
  }

  .bubble.assistant {
    background: var(--epfl-white);
    border: 1px solid var(--epfl-gray-100);
  }

  .bubble.system {
    background: var(--epfl-warning-bg);
    border: 1px solid var(--epfl-warning-border);
    font-size: 0.8rem;
    font-style: italic;
  }

  .text.user {
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  .att-images {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
    margin-top: 0.4rem;
  }

  .att-image {
    max-width: 160px;
    max-height: 160px;
    border-radius: var(--epfl-radius);
    border: 1px solid var(--epfl-gray-200);
    display: block;
  }

  .att-docs {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
    margin-top: 0.4rem;
  }

  .att-doc {
    font-size: 0.72rem;
    color: var(--epfl-accent-dark);
    background: var(--epfl-gray-100);
    border: 1px solid var(--epfl-gray-200);
    border-radius: var(--epfl-radius);
    padding: 0.15rem 0.4rem;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .att-doc-tk {
    color: var(--epfl-accent);
  }

  .text.assistant :global(p) {
    margin: 0 0 0.6rem;
  }

  .text.assistant :global(p:last-child) {
    margin-bottom: 0;
  }

  .text.assistant :global(ul),
  .text.assistant :global(ol) {
    margin: 0.3rem 0 0.6rem 1.2rem;
    padding: 0;
  }

  .text.assistant :global(li) {
    margin: 0.15rem 0;
  }

  .text.assistant :global(h1),
  .text.assistant :global(h2),
  .text.assistant :global(h3),
  .text.assistant :global(h4) {
    font-size: 0.95rem;
    font-weight: 600;
    margin: 0.5rem 0 0.3rem;
  }

  .text.assistant :global(code) {
    background: var(--epfl-gray-50);
    padding: 0.1rem 0.3rem;
    border-radius: var(--epfl-radius);
    font-size: 0.82rem;
    font-family: var(--epfl-font-mono);
  }

  .text.assistant :global(pre) {
    background: var(--epfl-gray-50);
    padding: 0.5rem;
    border-radius: var(--epfl-radius);
    overflow-x: auto;
    font-size: 0.78rem;
    margin: 0.4rem 0;
  }

  .text.assistant :global(pre code) {
    background: none;
    padding: 0;
  }

  .text.assistant :global(.mermaid-block) {
    margin: 0.6rem 0;
    border: 1px solid var(--epfl-gray-100);
    border-radius: var(--epfl-radius);
    background: var(--epfl-white);
    overflow: hidden;
  }

  .text.assistant :global(.mermaid-figure) {
    padding: 0.6rem;
    overflow: auto;
    max-height: 60vh;
    text-align: center;
  }

  .text.assistant :global(.mermaid-figure svg) {
    max-width: 100%;
    height: auto;
    display: inline-block;
  }

  .text.assistant :global(.mermaid-figure span.edgeLabel.citation) {
    background: transparent;
    padding: 0;
    border-radius: 0;
    font-size: inherit;
    color: inherit;
    font-weight: inherit;
    font-family: inherit;
    text-decoration: underline;
    text-decoration-style: dotted;
    text-decoration-color: var(--epfl-gray-600);
    text-decoration-thickness: 1px;
    text-underline-offset: 2px;
    cursor: pointer;
    transition: background 0.12s ease;
  }

  .text.assistant :global(.mermaid-figure span.edgeLabel.citation:hover) {
    background: var(--epfl-gray-100);
  }

  .text.assistant :global(.mermaid-toolbar) {
    display: flex;
    gap: 0.4rem;
    padding: 0.3rem 0.5rem;
    border-top: 1px solid var(--epfl-gray-100);
    background: var(--epfl-gray-50);
    justify-content: flex-end;
  }

  .text.assistant :global(.mermaid-toolbar button) {
    font-size: 0.72rem;
    padding: 0.2rem 0.5rem;
    border: 1px solid var(--epfl-gray-200);
    background: var(--epfl-white);
    border-radius: var(--epfl-radius);
    cursor: pointer;
    color: var(--epfl-gray-700);
    font-family: inherit;
  }

  .text.assistant :global(.mermaid-toolbar button:hover) {
    background: var(--epfl-gray-100);
    color: var(--epfl-text);
  }

  .text.assistant :global(.file-card) {
    margin: 0.6rem 0;
    border: 1px solid var(--epfl-gray-200);
    border-left: 3px solid var(--epfl-accent);
    border-radius: var(--epfl-radius);
    background: var(--epfl-white);
    overflow: hidden;
  }

  .text.assistant :global(.file-card-head) {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.45rem 0.6rem;
  }

  .text.assistant :global(.file-card-name) {
    flex: 1;
    min-width: 0;
    font-weight: 600;
    font-size: 0.82rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .text.assistant :global(.file-card-kind) {
    font-size: 0.7rem;
    color: var(--epfl-gray-600);
    background: var(--epfl-gray-100);
    border-radius: var(--epfl-radius);
    padding: 0.05rem 0.4rem;
  }

  .text.assistant :global(.file-card-head button) {
    font-size: 0.75rem;
    padding: 0.25rem 0.6rem;
    border: 1px solid var(--epfl-accent);
    background: var(--epfl-accent);
    color: var(--epfl-white);
    border-radius: var(--epfl-radius);
    cursor: pointer;
    font-family: inherit;
    white-space: nowrap;
  }

  .text.assistant :global(.file-card-head button:hover:not(:disabled)) {
    background: var(--epfl-accent-dark);
  }

  /* Révision Word : la copie modifiée, à côté du suivi des modifications. */
  .text.assistant :global(.file-card-head button.file-card-alt) {
    background: var(--epfl-white);
    color: var(--epfl-accent);
  }
  .text.assistant :global(.file-card-head button.file-card-alt:hover:not(:disabled)) {
    background: var(--epfl-gray-50);
  }
  .text.assistant :global(.file-card-head) {
    flex-wrap: wrap;
  }

  .text.assistant :global(.file-card-head button:disabled) {
    border-color: var(--epfl-gray-200);
    background: var(--epfl-gray-100);
    color: var(--epfl-gray-600);
    cursor: default;
  }

  .text.assistant :global(.file-card-preview) {
    border-top: 1px solid var(--epfl-gray-100);
    font-size: 0.75rem;
  }

  .text.assistant :global(.file-card-preview summary) {
    padding: 0.25rem 0.6rem;
    color: var(--epfl-gray-600);
    cursor: pointer;
  }

  .text.assistant :global(.file-card-preview pre) {
    margin: 0;
    border-radius: 0;
    max-height: 40vh;
    overflow: auto;
  }

  .text.assistant :global(.mermaid-error) {
    background: var(--epfl-danger-bg);
    color: var(--epfl-danger-text);
    padding: 0.4rem 0.6rem;
    border: 1px solid var(--epfl-danger-border);
    border-radius: var(--epfl-radius);
    margin: 0.4rem 0 0;
    font-size: 0.78rem;
    font-family: var(--epfl-font-mono);
    white-space: pre-wrap;
    word-break: break-word;
  }

  .text.assistant :global(strong) {
    font-weight: 600;
  }

  .text.assistant :global(em) {
    font-style: italic;
  }

  .text.assistant :global(blockquote) {
    border-left: 3px solid var(--epfl-gray-100);
    margin: 0.4rem 0;
    padding: 0.2rem 0 0.2rem 0.7rem;
    color: var(--epfl-gray-600);
  }

  .text.assistant :global(.citation) {
    display: inline;
    background: var(--epfl-gray-100);
    color: var(--epfl-accent-dark);
    padding: 0 0.25rem;
    border-radius: var(--epfl-radius);
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    user-select: none;
  }

  .text.assistant :global(.citation:hover) {
    background: var(--epfl-gray-200);
    text-decoration: underline;
  }

  .text.assistant :global(a) {
    color: var(--epfl-accent);
    text-decoration: underline;
  }

  .msg-actions {
    display: flex;
    gap: 0.4rem;
    margin-top: 0.5rem;
  }

  .msg-actions button {
    font-size: 0.72rem;
    padding: 0.18rem 0.5rem;
    border: 1px solid var(--epfl-gray-200);
    background: var(--epfl-white);
    border-radius: var(--epfl-radius);
    cursor: pointer;
    color: var(--epfl-gray-600);
    font-family: inherit;
  }

  .msg-actions button:hover:not(:disabled) {
    background: var(--epfl-gray-100);
    color: var(--epfl-text);
  }

  .msg-actions button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .sources {
    margin-top: 0.6rem;
    border-top: 1px solid var(--epfl-gray-100);
    padding-top: 0.4rem;
  }

  .sources summary {
    font-size: 0.75rem;
    color: var(--epfl-gray-600);
    cursor: pointer;
    user-select: none;
  }

  .sources ul {
    list-style: none;
    padding: 0.4rem 0 0 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .sources li {
    display: flex;
  }

  .source-row {
    display: flex;
    gap: 0.4rem;
    font-size: 0.75rem;
    color: var(--epfl-gray-600);
    background: none;
    border: none;
    padding: 0.2rem 0.3rem;
    border-radius: var(--epfl-radius);
    cursor: pointer;
    font-family: inherit;
    text-align: left;
    width: 100%;
    align-items: center;
  }

  .source-row:hover {
    background: var(--epfl-gray-100);
    color: var(--epfl-text);
  }

  .ref-num {
    color: var(--epfl-accent);
    font-weight: 600;
    min-width: 1.6rem;
  }

  .ref-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ref-score {
    color: var(--epfl-gray-400);
  }
</style>
